// MACHINERY V2 (Stroje) — the detail page's "KĽÚČOVÉ PARAMETRE" summary
// (M2.8).
//
// Spec: data package §11 — `specSections` row, "MachineSpecs on detail right
// panel; extractKeySpecs summary". Artboard 0902 draws the box under the
// identity card. Reference impl: components/CatalogModelDetailPage.tsx:70-89
// (`KEY_SPEC_KEYS`) + :91-105 (`extractKeySpecs`).
//
// RESTATED, NOT IMPORTED — and for the same reason `V2_DOC_TYPE_KEYS` is
// (readinessPresentation.ts): the function lives inside
// CatalogModelDetailPage.tsx, which imports MUI at :2 and `dt` at :17, and §3
// forbids MUI anywhere in the v2 import graph. The list and the matching rule
// are copied EXACTLY — same 18 keys in the same order, same
// case-insensitive `includes` match, same cap of 6, same
// `value + " " + unit` join when a unit is present.
//
// NOTE THE MATCH IS DELIBERATELY LOOSE. `"HP"` matches any specKey containing
// "hp", and the FIRST section-order hit wins. That is v1's behaviour and the
// summary is a convenience, not a contract — the authoritative, unit-converted
// values are in the full spec sheet beside it. Tightening it here would make
// the two panels disagree on the same page.
//
// NO UNIT CONVERSION HERE, ALSO BY PARITY. v1 renders the RAW specValue+unit in
// this box while the spec sheet beside it honours the metric/imperial toggle.
// Converting only this box would make the same number appear twice in two
// units under one heading.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { CatalogSpecSection } from "../../models/models";

export interface CatalogKeySpec {
  label: string;
  value: string;
}

const KEY_SPEC_KEYS = [
  "Max Spindle Speed",
  "Spindle Speed",
  "Spindle Power",
  "HP",
  "Max HP",
  "Taper Size",
  "Table Size",
  "Table Length",
  "# of Tools",
  "Number of Tools",
  "Tool Capacity",
  "Travels (X)",
  "X-Axis Travel",
  "Chuck Size",
  "Max Turning Diameter",
  "Max Turning Length",
  "Wheel Diameter",
  "Grinding Length",
];

const MAX_KEY_SPECS = 6;

export const extractKeySpecs = (sections?: CatalogSpecSection[]): CatalogKeySpec[] => {
  if (!sections || sections.length === 0) return [];

  const found: CatalogKeySpec[] = [];
  const allSpecs = sections.reduce(
    (acc, section) => acc.concat(section.specs || []),
    [] as CatalogSpecSection["specs"]
  );

  for (let i = 0; i < KEY_SPEC_KEYS.length; i++) {
    if (found.length >= MAX_KEY_SPECS) break;
    const needle = KEY_SPEC_KEYS[i].toLowerCase();
    const spec = allSpecs.find((s) => s.specKey.toLowerCase().indexOf(needle) >= 0);
    // `spec.specValue` is typed `string` (models.ts:262) but the column it maps
    // is NULLABLE (Machinery.EF/Migrations/20260215105452_AddMachineCatalog.cs:103
    // — `SpecValue = table.Column<string>(type: "nvarchar(max)", nullable: true)`),
    // so a matched spec can carry a runtime null the type does not admit — the
    // same type-drift class as the key-spec columns. Unguarded, the unit branch
    // below interpolated it and rendered the literal "null mm" on the detail
    // route. A key spec with no value is not a key spec: omit it, matching the
    // absent-value convention the cards use (OMIT, never a placeholder). The
    // truthiness test also drops an empty value, which would render a bare unit.
    if (spec && spec.specValue) {
      found.push({
        label: spec.specKey,
        value: spec.unit ? `${spec.specValue} ${spec.unit}` : spec.specValue,
      });
    }
  }

  return found;
};
